/**
 * What is said when an online appointment is moved or called off on a
 * workspace that has stopped making meeting links.
 *
 * A practitioner who disconnects Google Meet under My brand → Configuration
 * does not thereby cancel the calls already in the diary: those are held on
 * links that were made while it was on. So the three screens that can move or
 * call off an appointment — the client's Appointment tab, My task, and the
 * answer on the client screen's today strip — each have to say something
 * about it, and they have to say the same thing. The copy lives here rather
 * than in the three handlers for the usual reason: three copies of a sentence
 * is three places for it to drift.
 *
 * The two acts are not treated the same, because they are not the same act:
 *
 *   reschedule  blocked. Moving a call means making a new link for the new
 *               slot, and there is no app left to make one. Sending the
 *               request anyway asks the backend to move an appointment it
 *               cannot finish moving, and whatever it answers with is a
 *               server's account of a problem the screen already knew about.
 *
 *   cancel      allowed. Calling a session off does not need a working link,
 *               and a practitioner who cannot cancel because an integration
 *               is off is stuck in a way nothing justifies. It goes through
 *               and the toast adds what is left to do by hand.
 *
 * Neither says anything while the settings are still being read, or where the
 * read failed: `settings` is `undefined` in both cases, and blocking a move
 * on the strength of an answer nobody has would be worse than the backend's
 * own error.
 */

import { errorCode } from "../../lib/api";
import { ApiError } from "../../lib/http";
import { MEETING_APPS, type MeetingAppId } from "./meetingApps";
import type { MeetingAppsState } from "./hooks/useMeetingApps";
import type { AppointmentType } from "./types/appointments.types";

/**
 * As much of the hook as these two questions need. A `Pick` rather than the
 * whole state so a test can answer them with four fields rather than standing
 * up a query client.
 */
export type MeetingAppsAnswer = Pick<
  MeetingAppsState,
  "settings" | "anyConnected" | "isEnabled" | "isLinked"
>;

export interface MeetingLinkNotice {
  title: string;
  message: string;
}

/**
 * How long a toast carrying one of these stands, in ms.
 *
 * Longer than the toast's own 3s default, because these are the longest
 * messages the app raises: they name an app, say why the act could not be
 * finished or what is left to do, and say where to go. Three seconds is a
 * confirmation's worth of reading, not an instruction's, and a message that
 * leaves before it has been read is the same as no message. Here rather than
 * at the three call sites so the three cannot disagree.
 */
export const MEETING_NOTICE_MS = 8000;

/** The app's name as the practice reads it — see `MEETING_APPS`. */
const nameOf = (app: MeetingAppId): string =>
  MEETING_APPS.find((entry) => entry.id === app)?.name ?? "Your meeting app";

/**
 * The app the message is about — always one, never "a meeting app".
 *
 * Two readings, in order:
 *
 *   linked but not enabled   an account is still on file and the provider was
 *                            switched off, which is exactly what a disconnect
 *                            leaves behind: `PATCH /meeting-settings/` with
 *                            `MANUAL` turns a provider off and does not touch
 *                            the account. Once it is off the provider field
 *                            names nobody, so this is what remembers which.
 *
 *   the shipped app          everything else — a workspace that never linked
 *                            one, and a backend that clears the connection on
 *                            the way out. Google Meet is the one app that
 *                            ships (Zoom is behind its pilot, see
 *                            `MEETING_APPS`), so it is the one that could have
 *                            been made the link, and naming it is both honest
 *                            and the only useful thing to say.
 *
 * It used to fall back to "No meeting app is connected", which named nothing
 * and left the reader to work out which app and which screen. A message that
 * cannot say what to reconnect is not worth the room it takes.
 */
const subject = (apps: MeetingAppsAnswer): MeetingAppId => {
  const switchedOff = MEETING_APPS.find(
    (app) => apps.isLinked(app.id) && !apps.isEnabled(app.id)
  );
  if (switchedOff) return switchedOff.id;

  return MEETING_APPS.find((app) => app.available)?.id ?? "google-meet";
};

/**
 * Whether links have stopped being made, as far as this build can tell.
 *
 * `settings` is the guard, not `anyConnected` on its own: that is false while
 * the read is in flight and false again where it failed, and either would
 * block a reschedule on a workspace whose app is connected perfectly well.
 */
const noAppMakingLinks = (apps: MeetingAppsAnswer): boolean =>
  !!apps.settings && !apps.anyConnected;

/**
 * Why this appointment cannot be moved, or `null` where it can be.
 *
 * Two sentences at most: what is wrong, and the one thing to do about it. The
 * verb follows the account — an app that was switched off is reconnected,
 * one that was never linked is connected — because telling somebody to
 * reconnect something they never connected sends them looking for a state
 * they were never in.
 *
 * `null` for an in-person sitting whatever the settings say — a room does not
 * need an app — and `null` for every appointment on a workspace that still
 * has one connected.
 */
export const rescheduleBlockedNotice = (
  type: AppointmentType,
  apps: MeetingAppsAnswer
): MeetingLinkNotice | null => {
  if (type !== "Online" || !noAppMakingLinks(apps)) return null;

  const app = subject(apps);
  const name = nameOf(app);
  const everLinked = apps.isLinked(app);

  return {
    title: `${name} is ${everLinked ? "disconnected" : "not connected"}`,
    message: `Moving an online appointment needs a new meeting link. ${
      everLinked ? "Reconnect" : "Connect"
    } ${name} under My brand → Configuration, then reschedule.`,
  };
};

/**
 * The sentence a cancellation adds about the invitation nobody withdrew, or
 * `null` where there is nothing to add.
 *
 * A sentence rather than a notice of its own, because the cancellation
 * succeeded: the toast's title is still "Appointment cancelled" and this goes
 * on the end of what it already said, so one message carries the whole answer
 * rather than two cards arguing over the same corner of the screen.
 *
 * "May still be" rather than "is": what the backend does with the calendar
 * event on its side is its own business, and this is the frontend saying what
 * it cannot vouch for rather than reporting a fact it does not have.
 */
export const cancelledWithoutAppNote = (
  type: AppointmentType,
  apps: MeetingAppsAnswer
): string | null => {
  if (type !== "Online" || !noAppMakingLinks(apps)) return null;

  const app = subject(apps);
  return `${nameOf(app)} is ${
    apps.isLinked(app) ? "disconnected" : "not connected"
  }, so the meeting invite may still be in the calendar.`;
};

/**
 * The backend's name for a meeting it will not touch because it was made on
 * an account this workspace is not signed into any more.
 *
 * One code today. It is matched by name rather than by status, because a 400
 * from these endpoints is usually a clash or a slot in the past and those are
 * better said in the server's own words.
 */
const ACCOUNT_MISMATCH = "appointment_meeting_account_mismatch";

/** `GOOGLE_MEET` as the app the rest of this file names. */
const APP_OF_CODE: Record<string, MeetingAppId> = {
  GOOGLE_MEET: "google-meet",
  ZOOM: "zoom",
};

/**
 * Which app the failure was about, out of the envelope's `details.provider`.
 *
 * Read from the error rather than from the settings, because the two are
 * different questions: the settings say which app is connected now, and this
 * says which one made the meeting that cannot be touched. On the failure this
 * was written for they are the same app and different accounts, but a
 * workspace that has since switched providers would otherwise be told the
 * wrong one.
 */
const appInDetails = (error: ApiError): MeetingAppId | null => {
  const body = error.data;
  if (!body || typeof body !== "object") return null;

  const details = (body as { details?: unknown }).details;
  if (!details || typeof details !== "object") return null;

  const provider = (details as { provider?: unknown }).provider;
  return typeof provider === "string" ? APP_OF_CODE[provider] ?? null : null;
};

/**
 * What to say about a write the backend refused because the meeting belongs
 * to another account, or `null` for every other failure — which the caller
 * then reports in the server's own words, as it always did.
 *
 * Its own notice rather than the server's sentence, for the same reason the
 * two above exist: the sentence explains what happened and stops there, and
 * what the reader needs next is the tab where the account is changed. The
 * server's own message is good — this adds the way out of it.
 *
 * Deliberately not worded per act. The account is the problem whether the
 * appointment was being moved, called off or marked done, so one notice
 * covers all of them and each screen can ask this once, in the one place it
 * already handles a failed write.
 */
export const meetingAccountNotice = (
  error: unknown
): MeetingLinkNotice | null => {
  if (!(error instanceof ApiError) || errorCode(error) !== ACCOUNT_MISMATCH) {
    return null;
  }

  const name = nameOf(appInDetails(error) ?? "google-meet");
  return {
    title: `This meeting is on a different ${name} account`,
    message: `It was made on another ${name} account, so the one connected now cannot change it. Connect that account under My brand → Configuration, then try again.`,
  };
};
